import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * diseaseHighlightPolygons を抽出してパースする
 * 
 * 検索結果に含まれるハイライト座標情報をパースする。
 * 座標はインチ単位のポリゴン配列として格納されている。
 * 
 * @param {Array|string|null} rawPolygon - diseaseHighlightPolygons の値（配列または JSON 文字列）
 * @returns {Array|null} パース済みのポリゴン配列（[{x, y}, {x, y}, ...]）、または不正な場合は null
 */
const parseHighlightsPolygon = (rawPolygon) => {
  if (!rawPolygon) {
    return null;
  }

  let polygonData = rawPolygon;

  // 文字列の場合は JSON.parse する
  if (typeof polygonData === 'string') {
    try {
      polygonData = JSON.parse(polygonData);
    } catch (e) {
      console.warn('diseaseHighlightPolygons のパースに失敗しました:', e);
      return null;
    }
  }

  // 配列の場合のみ返す
  if (Array.isArray(polygonData)) {
    return polygonData;
  }

  return null;
};

/**
 * PDFハイライト描画を管理するカスタムフック
 * 
 * AI検索でヒットした箇所をPDF上にハイライト表示する機能を提供する。
 * Canvas要素を使用してPDFページ上にポリゴン（多角形）を描画する。
 * 
 * 座標変換の仕組み:
 * 1. APIから返される座標はインチ単位
 * 2. PDFはポイント単位（1インチ = 72ポイント）
 * 3. 描画時はCanvas のピクセル座標に変換
 * 
 * @param {Object} options - オプション
 * @param {Object} options.pageInfo - ページ情報（width, height をポイント単位で含む）
 * @param {number} options.currentPage - 現在表示中のページ番号
 * @param {number} options.zoomLevel - 現在のズームレベル（%）
 * @returns {Object} ハイライト関連の状態とrefs
 * @returns {Array|null} returns.highlightsPolygon - ハイライト用ポリゴン座標配列
 * @returns {number|null} returns.highlightTargetPage - ハイライト対象ページ番号
 * @returns {React.RefObject} returns.highlightCanvasRef - ハイライト描画用Canvas要素のref
 * @returns {React.RefObject} returns.pdfContainerRef - PDFコンテナ要素のref
 * @returns {Function} returns.initializeHighlight - ハイライト情報初期化関数
 * @returns {Function} returns.setHighlightsPolygon - ポリゴン設定関数
 * @returns {Function} returns.setHighlightTargetPage - 対象ページ設定関数
 */
export const usePdfHighlight = ({ pageInfo, currentPage, zoomLevel }) => {
  /**
   * ハイライト用ポリゴン座標配列
   * インチ単位の {x, y} 座標の配列
   */
  const [highlightsPolygon, setHighlightsPolygon] = useState(null);

  /**
   * ハイライト対象ページ番号
   * このページでのみハイライトが描画される
   */
  const [highlightTargetPage, setHighlightTargetPage] = useState(null);

  /**
   * ハイライト描画用Canvas要素のref
   * PDFページの上に重ねて配置され、ハイライトを描画する
   */
  const highlightCanvasRef = useRef(null);

  /**
   * PDFコンテナ要素のref
   * react-pdf のページ要素を含むコンテナ。
   * Canvas サイズの取得やObserver の設定に使用
   */
  const pdfContainerRef = useRef(null);

  /**
   * ハイライト情報を初期化する
   * 検索結果データからポリゴン座標と対象ページを抽出して設定
   * 
   * @param {Object} resultData - 検索結果データ
   * @param {string|Array} resultData.diseaseHighlightPolygons - ポリゴン座標（JSON文字列または配列）
   * @param {number|string} resultData.pages - ハイライト対象ページ番号
   */
  const initializeHighlight = useCallback((resultData) => {
    if (!resultData) return;

    // diseaseHighlightPolygons の抽出とパース
    setHighlightsPolygon(parseHighlightsPolygon(resultData.diseaseHighlightPolygons));

    // ハイライト対象ページの設定（pagesフィールドがハイライト表示対象ページ）
    if (resultData.pages) {
      // ページ番号を数値に変換
      const pageNum = typeof resultData.pages === 'number'
        ? resultData.pages
        : parseInt(resultData.pages, 10);
      
      //　有効なページ番号の場合に設定
      if (!isNaN(pageNum) && pageNum > 0) {
        setHighlightTargetPage(pageNum);
      }
    }
  }, []);

  /**
   * 必要に応じてcanvasをクリアする
   * ポリゴンがない場合、またはハイライト対象ページでない場合はクリアしてtrueを返す
   * 
   * @returns {boolean} クリアした場合はtrue（早期リターン用）
   */
  const clearCanvasIfNeeded = useCallback(() => {
    const shouldClear = !highlightsPolygon ||
      highlightsPolygon.length === 0 ||
      (highlightTargetPage !== null && currentPage !== highlightTargetPage);

    if (shouldClear && highlightCanvasRef.current) {
      const ctx = highlightCanvasRef.current.getContext('2d');
      ctx.clearRect(0, 0, highlightCanvasRef.current.width, highlightCanvasRef.current.height);
    }

    return shouldClear;
  }, [highlightsPolygon, highlightTargetPage, currentPage]);

  /**
   * ハイライトを描画する
   * Canvasにポリゴンを描画し、半透明の黄色で塗りつぶす
   */
  const drawHighlights = useCallback(() => {
    const canvas = highlightCanvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const pageCanvas = pdfContainerRef.current?.querySelector('.react-pdf__Page__canvas');

    if (!pageCanvas) return;

    // Canvas サイズを PDF canvas に合わせる（高解像度ディスプレイ対応）
    canvas.width = pageCanvas.width;
    canvas.height = pageCanvas.height;
    canvas.style.width = `${pageCanvas.clientWidth}px`;
    canvas.style.height = `${pageCanvas.clientHeight}px`;

    // 前回の描画をクリア
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // ハイライト対象ページでない場合は描画しない
    if (highlightTargetPage !== null && currentPage !== highlightTargetPage) {
      return;
    }

    // ポリゴンがない場合は描画しない
    if (!highlightsPolygon || !pageInfo) return;

    // スケール計算
    // pageInfo.width: PDFのポイント単位の幅（1インチ = 72ポイント）
    // pageCanvas.width: 実際のcanvasピクセル幅（スケールとデバイスピクセル比が適用済み）
    const actualScale = pageCanvas.width / pageInfo.width;

    // ポリゴンパスを描画
    // 座標変換: インチ → ポイント（×72） → ピクセル（×actualScale）
    ctx.beginPath();
    highlightsPolygon.forEach((point, i) => {
      const x = point.x * 72 * actualScale;
      const y = point.y * 72 * actualScale;
      if (i === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    });
    ctx.closePath();

    // 半透明の黄色で塗りつぶし
    ctx.fillStyle = 'rgba(255, 255, 0, 0.15)';
    ctx.fill();

    // オレンジ色の枠線
    ctx.strokeStyle = 'rgba(255, 200, 0, 0.8)';
    ctx.lineWidth = 2;
    ctx.stroke();
  }, [highlightsPolygon, highlightTargetPage, currentPage, pageInfo]);

  /**
   * ポリゴンハイライト描画Effect
   * 
   * 以下のタイミングで再描画が行われる:
   * - ポリゴンデータの変更
   * - ページ切り替え
   * - ズームレベルの変更
   * - PDFページのリサイズ
   */
  useEffect(() => {
    // ポリゴンがない場合、またはハイライト対象ページでない場合は canvas をクリア
    if (clearCanvasIfNeeded()) {
      return;
    }

    // 必要な要素が揃っていない場合は何もしない
    if (!pageInfo || !highlightCanvasRef.current || !pdfContainerRef.current) {
      return;
    }

    // PDF canvas 描画完了を待つため少し遅延させて描画
    const timeoutId = setTimeout(drawHighlights, 100);

    // PDF ページ再レンダリング追従（MutationObserver）
    // PDFページの属性変更やDOM変更を監視して再描画
    const observer = new MutationObserver(drawHighlights);
    const pageElement = pdfContainerRef.current.querySelector('.react-pdf__Page');
    if (pageElement) {
      observer.observe(pageElement, {
        attributes: true,
        childList: true,
        subtree: true,
      });
    }

    // リサイズ追従（ResizeObserver）
    // PDFページのサイズ変更を監視して再描画
    const resizeObserver = new ResizeObserver(drawHighlights);
    const pdfPageCanvas = pdfContainerRef.current.querySelector('.react-pdf__Page__canvas');
    if (pdfPageCanvas) {
      resizeObserver.observe(pdfPageCanvas);
    }

    // クリーンアップ: タイマーとObserverを解除
    return () => {
      clearTimeout(timeoutId);
      observer.disconnect();
      resizeObserver.disconnect();
    };
  }, [clearCanvasIfNeeded, drawHighlights, pageInfo, zoomLevel]);

  return {
    highlightsPolygon,
    highlightTargetPage,
    highlightCanvasRef,
    pdfContainerRef,
    initializeHighlight,
    setHighlightsPolygon,
    setHighlightTargetPage,
  };
};

export default usePdfHighlight;
